import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button, iconButton } from '../ui/Button';
import { field } from '../create/Field';
import { useCommitKey } from '../ui/NumberInput';
import { useTapOnce } from '../ui/useTapOnce';
import { useOnline } from '../ui/useConnection';
import { sendOnce } from '../tickets/sendOnce';
import { IconInventory, IconPlus, IconTrash } from '../icons';
import {
  UnresolvedState,
  queryState,
  unresolved,
  type QueryPhase,
} from '../ui/QueryStates';
import { firstName } from '../schedule/crewPins';

/** The office's read (`jobParts.list`), typed by the router. Quantities in hundredths. */
export type JobPartsList = inferOutput<TRPCOptionsProxy<AppRouter>['jobParts']['list']>;
type Line = JobPartsList['lines'][number];
type Tech = JobPartsList['techs'][number];
/** One part "Plan a part" offers (`jobParts.itemChoices`). */
export type PartChoice = inferOutput<
  TRPCOptionsProxy<AppRouter>['jobParts']['itemChoices']
>[number];

/**
 * The button that plans one. Not "Add a part": that is the parts-used card's, right below,
 * which takes the part off a van and bills it. This one only plans.
 */
export const PLAN_A_PART = 'Plan a part';
export const NO_HOLDS_NOTE =
  'Planned parts reserve nothing — other jobs may draw on the same stock.';
export const EMPTY_LIST = 'No parts planned. Add the parts this job will need.';
export const ON_THE_LIST = 'Already on the list — change its quantity on its row.';
export const OFFLINE_EDITS = 'Reconnect to edit planned parts.';
export const ALL_LISTED =
  'Every part here is on the list already. Change its quantity on its row.';

/**
 * "Parts for this job" (ops insights Phase 6a, spec §4): the parts the job is expected to use,
 * how many are used so far and left, and whether the assigned techs' vans and the shop hold
 * what is left. A PLAN: it reserves nothing and bills nothing (the parts-used card and the
 * invoice read what was actually used).
 *
 * The server decides every number here (`partsNeed`) and refuses every edit it won't take
 * (`jobParts.add` and its siblings); this card draws the answer and sends what was asked. Owner
 * and office only: the read and the edits are `job:update`, so another role sees no card.
 */
export function JobPartsCard({
  jobId,
  closedReason,
}: {
  jobId: string;
  /**
   * Why this job takes no more parts (`partsClosedReason(job)`), or null while it does. The
   * list is then read-only, with this sentence: the server refuses every edit with it.
   */
  closedReason: string | null;
}) {
  const { role } = useSession();
  if (!can(role, 'job', 'update')) return null;
  return <JobPartsLoader jobId={jobId} closedReason={closedReason} />;
}

function JobPartsLoader({
  jobId,
  closedReason,
}: {
  jobId: string;
  closedReason: string | null;
}) {
  const trpc = useTRPC();
  const list = useQuery(trpc.jobParts.list.queryOptions({ jobId }));
  if (list.data) {
    return <JobPartsBody jobId={jobId} data={list.data} closedReason={closedReason} />;
  }
  // The shared four states: loading, offline (no signal, not "failed"), forbidden, failed.
  return (
    <SectionCard title="Parts for this job">
      <UnresolvedState
        state={unresolved(queryState(list))}
        what="the parts planned for this job"
        icon={IconInventory}
        error={list.error}
        onRetry={() => void list.refetch()}
        skeleton="h-16"
      />
    </SectionCard>
  );
}

/**
 * The edits go now or fail where they were made: `networkMode: 'always'` never PAUSES a write
 * (the app's default, `offlineFirst`, pauses one whose signal drops and sends it on
 * reconnect; `'online'` pauses one fired offline). Fired offline is prevented above (the
 * controls go); a signal lost mid-write fails it, visibly, on its row.
 */
const ONLINE_ONLY = {
  networkMode: 'always',
  // Not the app's `shouldRetryWrite`: that sends a REFUSAL (a duplicate, a stale quantity, a
  // bound, a 403) four times over ~7 s before the card can say it (Phase 6a QA D2). An
  // online-only edit the server refused is said at once; a person can tap again.
  retry: false,
} as const;

/**
 * A number box that is REPLACED by what is typed: focused (by the keyboard, a click, or its
 * own `autoFocus`), its whole value is selected. "How many" starts at 1 with the caret after
 * it, so typing 3 planned 13 (Phase 6a QA, D1).
 */
const selectAll = (e: { currentTarget: { select: () => void } }) =>
  e.currentTarget.select();

/**
 * A box sized by its own width (`field` is a full-width form row): the planned quantity and
 * "How many" are a few digits wide.
 */
const smallBox =
  'h-9 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm text-text';

/** What a result says once it lands: a sentence, and how it reads to a screen reader. */
type Said = { tone: 'success' | 'neutral'; text: string } | null;

/** The card for a read that has answered. Exported so tests render it with data. */
export function JobPartsBody({
  jobId,
  data,
  closedReason,
}: {
  jobId: string;
  data: JobPartsList;
  closedReason: string | null;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  /**
   * Planning is a desk's job, done online: these edits are not offline writes (they are not on
   * `REPLAYABLE`, and must not wait in memory to land later over someone else's plan). With
   * no signal the list is read-only and says so (`OFFLINE_EDITS`).
   */
  const online = useOnline();
  const editable = closedReason === null && online;
  const [adding, setAdding] = useState(false);
  const [said, setSaid] = useState<Said>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const body = useRef<HTMLDivElement>(null);
  /**
   * A removed row takes its Remove button with it, and the browser would drop the focus to the
   * page. It goes to the next row's Remove, else the previous row's (named when it was tapped:
   * the list has been read again by now), else the open panel's field, else "Plan a part".
   */
  const focusAfterRemove = (near: { next: string | null; prev: string | null }) => {
    const at = (selector: string) =>
      body.current?.querySelector<HTMLElement>(selector) ?? null;
    const target =
      (near.next ? at(`[data-remove="${near.next}"]`) : null) ??
      (near.prev ? at(`[data-remove="${near.prev}"]`) : null) ??
      at('[data-plan-field="true"]') ??
      addButton.current;
    target?.focus();
  };
  /** Read again, and stay pending until it has: a write's "done" is the list showing it. */
  const reread = () => qc.invalidateQueries(trpc.jobParts.list.queryFilter({ jobId }));

  /** "Add the quote's parts": one write per tap (`useTapOnce`), its answer said in the card. */
  const quoteLock = useTapOnce();
  const fromQuote = useMutation(
    trpc.jobParts.addFromQuote.mutationOptions({
      ...ONLINE_ONLY,
      onSettled: () => quoteLock.release(),
      // A refusal often means the list moved under this tab (another office, a close): read it
      // again, so what is drawn is what the server holds. Every edit below does the same.
      onError: () => void reread(),
      onSuccess: (r) => {
        setSaid({
          tone: r.added > 0 ? 'success' : 'neutral',
          text: quoteResult(r, data),
        });
        return reread();
      },
    }),
  );

  /**
   * Every edit starts by clearing what the card last said: "Added Contactor 30A." above a list
   * the next edit has changed, or a refusal of the quote's parts, would be a claim gone stale.
   */
  const clearSaid = () => {
    setSaid(null);
    fromQuote.reset();
  };

  const listed = useMemo(() => new Set(data.lines.map((l) => l.item.id)), [data.lines]);
  const title =
    data.lines.length === 0
      ? 'Parts for this job'
      : `Parts for this job (${data.lines.length})`;

  return (
    <SectionCard
      title={title}
      right={
        editable && !adding ? (
          <Button
            ref={addButton}
            variant="ghost"
            onClick={() => {
              clearSaid();
              setAdding(true);
            }}
          >
            <IconPlus size={14} />
            {PLAN_A_PART}
          </Button>
        ) : null
      }
    >
      <div ref={body} className="@container space-y-3">
        {closedReason ? <Notice tone="neutral">{closedReason}</Notice> : null}
        {!closedReason && !online ? <Notice tone="warn">{OFFLINE_EDITS}</Notice> : null}
        {fromQuote.error ? (
          <Notice tone="danger" alert>
            {fromQuote.error.message}
          </Notice>
        ) : null}
        <div role="status">
          {said ? <Notice tone={said.tone}>{said.text}</Notice> : null}
        </div>

        {adding && editable ? (
          <AddPart
            jobId={jobId}
            listed={listed}
            onDone={(text) => {
              setAdding(false);
              if (text) setSaid({ tone: 'success', text });
              // The panel goes; the focus goes back to what opened it (it unmounted with it).
              requestAnimationFrame(() => addButton.current?.focus());
            }}
            reread={reread}
          />
        ) : null}

        {data.lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">{EMPTY_LIST}</p>
        ) : (
          <ul className="divide-y divide-border text-sm" aria-label="Planned parts">
            {data.lines.map((line, i) => (
              <PartRow
                key={line.lineId}
                line={line}
                techs={data.techs}
                editable={editable}
                reread={reread}
                onEdit={clearSaid}
                onRemoved={() =>
                  focusAfterRemove({
                    next: data.lines[i + 1]?.lineId ?? null,
                    prev: data.lines[i - 1]?.lineId ?? null,
                  })
                }
              />
            ))}
          </ul>
        )}

        {editable && data.quote ? (
          <Button
            variant="ghost"
            disabled={fromQuote.isPending}
            onClick={() => {
              clearSaid();
              sendOnce(quoteLock, fromQuote.mutate, { jobId });
            }}
          >
            {fromQuote.isPending ? 'Adding…' : "Add the quote's parts"}
          </Button>
        ) : null}

        <p className="text-xs text-muted-foreground">{NO_HOLDS_NOTE}</p>
      </div>
    </SectionCard>
  );
}

/** What "Add the quote's parts" did, in a sentence. */
export function quoteResult(
  r: { added: number; alreadyListed: number },
  data: Pick<JobPartsList, 'quote'>,
): string {
  const quote = data.quote ? `Quote #${data.quote.quoteNumber}` : 'the quote';
  if (r.added > 0) {
    return `Added ${r.added} ${r.added === 1 ? 'part' : 'parts'} from ${quote}.`;
  }
  if (r.alreadyListed > 0)
    return `${capital(quote)}'s parts are all on the list already.`;
  return `${capital(quote)} has no stocked parts to add.`;
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Ana" from "Ana Reyes" (the crew map's `firstName`); a tech with no name is "A tech". */
const techName = (name: string | null): string =>
  (name?.trim() ? firstName(name) : '') || 'A tech';

/**
 * Where a part's "left" can come from, in words: each counted tech's van, then the shop
 * ("Ana's van 2 · Shop stock 5"), and, apart, what this job alone would leave short ("Short 1 for
 * this job"). No live visit means no vans: the shop alone. Every figure is the server's.
 */
export function availability(
  line: Pick<
    Line,
    'leftHundredths' | 'vans' | 'shopHundredths' | 'shortForThisJobHundredths'
  >,
  techs: Pick<Tech, 'userId' | 'name'>[],
): { holds: string; short: string | null } {
  if (line.leftHundredths === 0) return { holds: 'All used', short: null };
  const vans = techs.map((t) => {
    const held = line.vans.find((v) => v.userId === t.userId)?.hundredths ?? null;
    return held === null
      ? `${techName(t.name)}: no van`
      : `${techName(t.name)}'s van ${formatQty(held)}`;
  });
  const holds = [
    ...(techs.length === 0 ? ['No visit booked'] : vans),
    // "Shop stock" (CONTEXT.md): "the shop" alone is the business, not a place parts sit.
    `Shop stock ${formatQty(line.shopHundredths)}`,
  ].join(' · ');
  const short =
    line.shortForThisJobHundredths > 0
      ? `Short ${formatQty(line.shortForThisJobHundredths)} for this job`
      : null;
  return { holds, short };
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-12 text-xs text-muted-foreground">
      <span className="block">{label}</span>
      <span className="block text-sm font-medium tabular-nums text-text">{value}</span>
    </div>
  );
}

/** One planned part: its numbers, its quantity edited in place, and Remove. */
function PartRow({
  line,
  techs,
  editable,
  reread,
  onEdit,
  onRemoved,
}: {
  line: Line;
  techs: Tech[];
  editable: boolean;
  reread: () => Promise<void>;
  /** An edit is going out: the card's last word no longer holds. */
  onEdit: () => void;
  /** This row is gone: where the focus goes (the body knows its neighbours). */
  onRemoved: () => void;
}) {
  const trpc = useTRPC();
  const { holds, short } = availability(line, techs);
  const name = line.item.name;

  /**
   * The quantity, saved on blur (Enter blurs). What was sent shows until the list is read
   * again; a refusal puts the server's number back (`useCommitKey`) and says why.
   */
  const [sent, setSent] = useState<string | null>(null);
  const qtyLock = useTapOnce();
  const setQty = useMutation(
    trpc.jobParts.setQty.mutationOptions({
      ...ONLINE_ONLY,
      onSettled: () => {
        qtyLock.release();
        setSent(null);
      },
      onError: () => void reread(),
      onSuccess: () => reread(),
    }),
  );
  const shown = sent ?? formatQty(line.plannedHundredths);
  const [commitKey, redisplay] = useCommitKey(shown);

  const removeLock = useTapOnce();
  const remove = useMutation(
    trpc.jobParts.remove.mutationOptions({
      ...ONLINE_ONLY,
      onSettled: () => removeLock.release(),
      onError: () => void reread(),
      onSuccess: async () => {
        await reread();
        onRemoved();
      },
    }),
  );

  const commit = (text: string) => {
    const typed = text.trim();
    const n = Number(typed);
    redisplay();
    // Not a number: nothing to send, and the box shows the planned number again.
    if (typed === '' || !Number.isFinite(n)) return;
    if (Math.round(n * 100) === line.plannedHundredths && sent === null) return;
    setQty.reset();
    onEdit();
    // With the number this row showed: a newer one saved elsewhere is refused, not overwritten,
    // and the refusal reads the list again (`onError`).
    const sentWith = {
      lineId: line.lineId,
      qty: n,
      expectedQtyHundredths: line.plannedHundredths,
    };
    if (sendOnce(qtyLock, setQty.mutate, sentWith)) {
      setSent(typed);
    }
  };

  const error = setQty.error ?? remove.error;
  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 @lg:flex-row @lg:items-center @lg:gap-4">
      <div className="min-w-0 flex-1">
        <p className="font-medium break-words">{name}</p>
        {line.item.sku ? (
          <p className="text-xs text-muted-foreground">{line.item.sku}</p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {holds}
          {short ? (
            <>
              {' · '}
              <span className="font-semibold text-[var(--sw-warn-text)]">{short}</span>
            </>
          ) : null}
        </p>
        {error ? (
          <p className="mt-1 text-xs text-[var(--sw-danger-text)]" role="alert">
            {error.message}
          </p>
        ) : null}
      </div>
      <div className="flex items-end gap-4">
        {editable ? (
          <label className="text-xs text-muted-foreground">
            <span className="block">Planned</span>
            <input
              // ⚠️ Load-bearing, not a list key: a refused or unparsable entry is redisplayed.
              key={commitKey}
              className={`${smallBox} w-20 text-right tabular-nums`}
              inputMode="decimal"
              aria-label={`Planned: ${name}`}
              defaultValue={shown}
              readOnly={setQty.isPending}
              onFocus={selectAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  e.currentTarget.value = shown;
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => commit(e.currentTarget.value)}
            />
          </label>
        ) : (
          <Figure label="Planned" value={formatQty(line.plannedHundredths)} />
        )}
        <Figure label="Used" value={formatQty(line.usedHundredths)} />
        <Figure label="Left" value={formatQty(line.leftHundredths)} />
        {editable ? (
          <button
            type="button"
            className={iconButton}
            aria-label={`Remove ${name} from the list`}
            data-remove={line.lineId}
            title="Remove from the list"
            disabled={remove.isPending}
            onClick={() => {
              remove.reset();
              onEdit();
              sendOnce(removeLock, remove.mutate, { lineId: line.lineId });
            }}
          >
            <IconTrash size={16} />
          </button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * "Plan a part": a dedicated picker over this shop's tracked parts (`jobParts.itemChoices`), which
 * hands back an item id, never a typed name. A part already listed is shown and can't be
 * picked (its quantity is changed on its row); the server refuses it anyway.
 */
function AddPart({
  jobId,
  listed,
  onDone,
  reread,
}: {
  jobId: string;
  listed: ReadonlySet<string>;
  onDone: (said: string | null) => void;
  reread: () => Promise<void>;
}) {
  const trpc = useTRPC();
  const choices = useQuery(trpc.jobParts.itemChoices.queryOptions());
  const [picked, setPicked] = useState<PartChoice | null>(null);
  const [qty, setQty] = useState('1');
  const lock = useTapOnce();
  const add = useMutation(
    trpc.jobParts.add.mutationOptions({
      ...ONLINE_ONLY,
      onSettled: () => lock.release(),
      onError: () => void reread(),
      onSuccess: async () => {
        await reread();
        onDone(`Added ${picked?.name ?? 'the part'}.`);
      },
    }),
  );
  const n = Number(qty.trim());
  const sendable = picked !== null && qty.trim() !== '' && Number.isFinite(n);

  return (
    <div className="space-y-3 rounded-[var(--radius)] border border-border bg-surface-2 p-3">
      {add.error ? (
        <Notice tone="danger" alert>
          {add.error.message}
        </Notice>
      ) : null}
      {picked ? (
        <form
          noValidate
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!sendable) return;
            add.reset();
            sendOnce(lock, add.mutate, { jobId, itemId: picked.id, qty: n });
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium break-words text-sm">{picked.name}</p>
              {picked.sku ? (
                <p className="text-xs text-muted-foreground">{picked.sku}</p>
              ) : null}
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={add.isPending}
              onClick={() => {
                add.reset();
                setPicked(null);
              }}
            >
              Change
            </Button>
          </div>
          <label className="block text-xs font-medium text-muted-foreground">
            <span className="mb-1 block">How many</span>
            <input
              className={`${smallBox} block h-10 w-28`}
              inputMode="decimal"
              value={qty}
              readOnly={add.isPending}
              onChange={(e) => setQty(e.target.value)}
              onFocus={selectAll}
              data-plan-field="true"
              autoFocus
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={!sendable || add.isPending}>
              {add.isPending ? 'Adding…' : 'Add to the list'}
            </Button>
            <Button variant="ghost" disabled={add.isPending} onClick={() => onDone(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <>
          <PartPicker
            choices={choices.data ?? []}
            state={choices.data ? 'ready' : queryState(choices)}
            error={choices.error}
            onRetry={() => void choices.refetch()}
            listed={listed}
            onPick={(c) => {
              add.reset();
              setPicked(c);
            }}
            onCancel={() => onDone(null)}
          />
          <Button variant="ghost" onClick={() => onDone(null)}>
            Cancel
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * A combobox over the tracked parts: type to narrow, ↑/↓ to move, Enter to pick, Escape to
 * clear the search (or close, when it is empty). The listbox is always drawn under the box
 * (an inline panel, not a popup), so a pointer and a keyboard see the same list.
 */
export function PartPicker({
  choices,
  state,
  error,
  onRetry,
  listed,
  onPick,
  onCancel,
}: {
  choices: PartChoice[];
  /** The picker's read (`itemChoices`): its parts are drawn only once it is `ready`. */
  state: QueryPhase;
  error?: unknown;
  onRetry: () => void;
  listed: ReadonlySet<string>;
  onPick: (choice: PartChoice) => void;
  onCancel: () => void;
}) {
  const [term, setTerm] = useState('');
  /** The option the keyboard is on, by index into `matches`; null: the first one to pick. */
  const [active, setActive] = useState<number | null>(null);
  /** Enter (or a click) on a part already listed: said, not picked. */
  const [refused, setRefused] = useState(false);
  const inputId = useId();
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    return choices.filter(
      (c) =>
        !q || c.name.toLowerCase().includes(q) || (c.sku ?? '').toLowerCase().includes(q),
    );
  }, [choices, term]);
  const pickable = matches.filter((c) => !listed.has(c.id));
  /**
   * The arrows move over EVERY match, listed ones included, so a keyboard reader hears them
   * ("On the list") as a pointer sees them; the keyboard starts on the first it can pick.
   */
  const firstPickable = Math.max(
    matches.findIndex((c) => !listed.has(c.id)),
    0,
  );
  const last = Math.max(matches.length - 1, 0);
  const index = Math.min(active ?? firstPickable, last);
  const current = matches[index] ?? null;
  const optionId = (id: string) => `${listId}-${id}`;
  const choose = (c: PartChoice) => {
    if (listed.has(c.id)) setRefused(true);
    else onPick(c);
  };
  const move = (to: number) => {
    setRefused(false);
    setActive(Math.min(Math.max(to, 0), last));
  };

  useEffect(() => {
    setActive(null);
    setRefused(false);
  }, [term]);
  useEffect(() => {
    if (!current) return;
    const el = listRef.current?.querySelector?.(`[data-option="${current.id}"]`);
    (el as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [current]);

  return (
    <div className="space-y-2">
      <label
        className="block text-xs font-medium text-muted-foreground"
        htmlFor={inputId}
      >
        Part
      </label>
      <input
        id={inputId}
        className={`${field} text-text`}
        role="combobox"
        data-plan-field="true"
        aria-expanded="true"
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={current ? optionId(current.id) : undefined}
        placeholder="Search stocked parts by name or SKU"
        autoComplete="off"
        autoFocus
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            move(index + 1);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            move(index - 1);
          } else if (e.key === 'Home') {
            e.preventDefault();
            move(0);
          } else if (e.key === 'End') {
            e.preventDefault();
            move(last);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (current) choose(current);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            if (term) setTerm('');
            else onCancel();
          }
        }}
      />
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          what="the stocked parts"
          icon={IconInventory}
          error={error}
          onRetry={onRetry}
          skeleton="h-10"
        />
      ) : matches.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {choices.length === 0
            ? "You're not counting any parts yet. Turn on tracking for a part in Inventory first."
            : 'Nothing matches that.'}
        </p>
      ) : pickable.length === 0 ? (
        <p className="text-sm text-muted-foreground">{ALL_LISTED}</p>
      ) : null}
      <p role="status" className="text-sm text-muted-foreground empty:hidden">
        {refused ? ON_THE_LIST : null}
      </p>
      <ul
        id={listId}
        ref={listRef}
        role="listbox"
        aria-label="Stocked parts"
        className="max-h-60 overflow-y-auto rounded-[var(--radius)] border border-border bg-surface text-sm empty:hidden"
      >
        {matches.map((c) => {
          const onList = listed.has(c.id);
          const isActive = current?.id === c.id;
          return (
            <li
              key={c.id}
              id={optionId(c.id)}
              data-option={c.id}
              role="option"
              aria-selected={isActive}
              aria-disabled={onList || undefined}
              className={`flex cursor-pointer items-center gap-3 px-3 py-2 ${
                isActive
                  ? 'bg-surface-2 outline outline-2 -outline-offset-2 outline-brand'
                  : ''
              } ${onList ? 'cursor-default text-muted-foreground' : 'hover:bg-surface-2'}`}
              // Keeps the focus in the box: the list is driven from it.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(c)}
            >
              <span className="min-w-0 flex-1">
                <span className="block break-words font-medium">{c.name}</span>
                {c.sku ? (
                  <span className="block text-xs text-muted-foreground">{c.sku}</span>
                ) : null}
              </span>
              {onList ? <span className="shrink-0 text-xs">On the list</span> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
